import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Zap, BellRing, MapPin, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import GigCard from '../components/GigCard';
import ServicesTabs from '../components/ServicesTabs';
import { GIG_CATEGORIES, GIG_REGIONS } from '../lib/gigMockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GigsPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [region, setRegion] = useState('all');
  const [category, setCategory] = useState('all');
  const [radius, setRadius] = useState([15]);
  const [tab, setTab] = useState('available');
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionOpts, setRegionOpts] = useState([]);

  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  useEffect(() => {
    axios.get(`${API}/locations`, { params: { type: 'region', limit: 20 } })
      .then(({ data }) => setRegionOpts(data.locations || [])).catch(() => {});
  }, []);

  const fetchGigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'posted') params.mine = 'posted';
      else if (tab === 'working') params.mine = 'working';
      else {
        params.status = 'open';
        if (region !== 'all') params.region = region;
        if (category !== 'all') params.category = category;
      }
      const { data } = await axios.get(`${API}/gigs`, { params, withCredentials: true });
      setGigs(data);
    } catch (e) {
      setGigs([]);
    } finally {
      setLoading(false);
    }
  }, [tab, region, category]);

  useEffect(() => { fetchGigs(); }, [fetchGigs]);

  // Radius is client-side: only constrains gigs that carry a computed distance.
  const visible = gigs.filter((g) => g.distance_km == null || g.distance_km <= radius[0]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="gigs-page">
      {/* Services / Gigs tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-2">
        <h1 className="font-outfit text-3xl font-bold text-[#121212] mb-4">{tr('Pakalpojumi', 'Services', 'Услуги')}</h1>
        <ServicesTabs active="gigs" />
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#121212] to-[#2a2a2a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-2 text-[#D97736] font-semibold mb-3">
            <Zap className="w-5 h-5" />
            <span className="uppercase tracking-wider text-sm">{tr('Haltūras', 'Gigs', 'Халтуры')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-outfit mb-4 max-w-2xl">
            {tr('Atrodi darbu tepat tuvumā', 'Find work right near you', 'Найди работу рядом')}
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl mb-6 font-figtree">
            {tr(
              'Ātrie darbi pēc pieprasījuma — pārcelšanās, uzkopšana, remonts un vairāk. Pieņem darbu dažos klikšķos.',
              'On-demand quick jobs — moving, cleaning, repairs and more. Accept a gig in a few clicks.',
              'Быстрая работа по запросу — переезд, уборка, ремонт и больше.'
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/gigs/create">
              <Button className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="create-gig-btn">
                <Plus className="w-4 h-4 mr-1" />
                {tr('Izveidot haltūru', 'Post a gig', 'Создать халтуру')}
              </Button>
            </Link>
          </div>

          <div className="mt-8 flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4 max-w-2xl" data-testid="location-match-banner">
            <BellRing className="w-5 h-5 text-[#D97736] shrink-0 mt-0.5" />
            <p className="text-sm text-white/80 font-figtree">
              {tr(
                `Kā strādnieks tu automātiski saņemsi paziņojumus par jauniem darbiem ${radius[0]} km rādiusā no tavas atrašanās vietas.`,
                `As a worker you'll automatically get notified about new gigs within ${radius[0]} km of your location.`,
                `Как исполнитель вы автоматически получите уведомления о работе в радиусе ${radius[0]} км.`
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Filters + Feed */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList data-testid="gig-tabs">
            <TabsTrigger value="available" data-testid="tab-available">
              {tr('Pieejamie darbi', 'Available gigs', 'Доступные')}
            </TabsTrigger>
            {user && (
              <TabsTrigger value="posted" data-testid="tab-posted">
                {tr('Manis izveidotie', 'Posted by me', 'Мои заказы')}
              </TabsTrigger>
            )}
            {user && (
              <TabsTrigger value="working" data-testid="tab-working">
                {tr('Mani pieņemtie', 'Accepted by me', 'Принятые мной')}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filter sidebar — only on the available feed */}
          {tab === 'available' && (
            <aside className="lg:col-span-1 space-y-6 bg-white border border-[#E5E4E0] rounded-xl p-5 h-fit lg:sticky lg:top-20" data-testid="gig-filters">
              <div className="flex items-center gap-2 text-[#121212] font-semibold font-outfit">
                <SlidersHorizontal className="w-4 h-4" />
                {tr('Filtri', 'Filters', 'Фильтры')}
              </div>

              <div>
                <label className="text-sm font-medium text-[#5C5C5C] mb-2 block">{tr('Reģions', 'Region', 'Регион')}</label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="filter-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{tr('Visi reģioni', 'All regions', 'Все регионы')}</SelectItem>
                    {regionOpts.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-[#5C5C5C] mb-2 block">{tr('Kategorija', 'Category', 'Категория')}</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{tr('Visas kategorijas', 'All categories', 'Все категории')}</SelectItem>
                    {Object.entries(GIG_CATEGORIES).map(([key, c]) => (
                      <SelectItem key={key} value={key}>{c[language] || c.lv}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[#5C5C5C] flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {tr('Rādiuss', 'Radius', 'Радиус')}
                  </label>
                  <span className="text-sm font-semibold text-[#D97736]">{radius[0]} km</span>
                </div>
                <Slider value={radius} onValueChange={setRadius} min={1} max={50} step={1} data-testid="filter-radius" />
              </div>
            </aside>
          )}

          {/* Feed */}
          <div className={tab === 'available' ? 'lg:col-span-3' : 'lg:col-span-4'}>
            <p className="text-sm text-[#5C5C5C] mb-4" data-testid="gig-count">
              {loading ? tr('Ielādē...', 'Loading...', 'Загрузка...') : `${visible.length} ${tr('darbi atrasti', 'gigs found', 'работ найдено')}`}
            </p>
            {loading ? (
              <div className="grid sm:grid-cols-2 gap-5">
                {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl h-56 animate-pulse" />)}
              </div>
            ) : visible.length === 0 ? (
              <div className="bg-white border border-dashed border-[#E5E4E0] rounded-xl p-12 text-center text-[#5C5C5C]" data-testid="gig-empty">
                {tr('Nav darbu pēc šiem filtriem.', 'No gigs match these filters.', 'Нет работ по этим фильтрам.')}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {visible.map((gig) => <GigCard key={gig.id} gig={gig} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
